// Data hooks used by pages/components. Swapping mock → API happens in
// lib/api/client.ts; these signatures stay the same.
import { keepPreviousData, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api, avatarApi } from '@/lib/api/client';
import { fetchMarketMood } from '@/lib/api/marketMood';
import { MOCK_DATA } from '@/lib/api/mode';
import type { NewsQuery, SocialQuery } from '@/lib/api/real';
import type { Account, Alerts, Market, Portfolio, Profile } from '@/types';

/** the account's profile photo (data URL) or null — never blocks the page while it loads */
export const useAvatar = () => useQuery({ queryKey: ['avatar'], queryFn: avatarApi.get, staleTime: Infinity, retry: false }).data?.avatar ?? null;
export const useAccount = () => useSuspenseQuery({ queryKey: ['account'], queryFn: api.account }).data;
/** Market quotes; `watchlist` = the user's coins (in the order chosen in the account). */
export function useMarket(): Market {
  const m = useSuspenseQuery({ queryKey: ['market'], queryFn: api.market }).data;
  const { coins } = useAccount();
  return useMemo(() => ({ ...m, watchlist: coins.map((c) => m.universe.find((x) => x.symbol === c)).filter((x) => !!x) }), [m, coins]);
}
export const useDashboard = () => useSuspenseQuery({ queryKey: ['dashboard'], queryFn: api.dashboard }).data;
export const usePortfolioData = () => useSuspenseQuery({ queryKey: ['portfolio'], queryFn: api.portfolio }).data;
export const useSignals = () => useSuspenseQuery({ queryKey: ['signals'], queryFn: api.signals }).data;
export const useCalendar = () => useSuspenseQuery({ queryKey: ['calendar'], queryFn: api.calendar }).data;
/** one page of the News page; the previous page stays on screen while the next one loads */
export const useNewsPage = (q: NewsQuery, enabled = true) => {
  const tz = useAccount().web.displayTimezone;
  return useQuery({ queryKey: ['news-page', q, tz], queryFn: () => api.newsPage(q, tz), placeholderData: keepPreviousData, enabled });
};
/** today's newest three and today's count (Overview, counters) — one small page, shared */
export const TODAY_NEWS: NewsQuery = { scope: 'today', coin: 'all', cat: 'all', page: 1, perPage: 3 };
export const useNewsToday = () => useNewsPage(TODAY_NEWS);
export const useIdeas = () => useQuery({ queryKey: ['ideas'], queryFn: api.ideas }).data ?? [];
export const useWallStreet = () => useSuspenseQuery({ queryKey: ['wall-street'], queryFn: api.wallStreet }).data;
/** the crypto Fear & Greed index (ORCA API mode): cached by the site's market server; here a
 *  10-minute freshness and a quiet refresh every 15 minutes — a failure leaves the hero without it */
export const useMarketMood = () => useQuery({ queryKey: ['market-mood'], queryFn: fetchMarketMood, enabled: !MOCK_DATA,
  staleTime: 10 * 60_000, refetchInterval: 15 * 60_000, retry: 1 });
/** one server page of X Radar / YouTube; the previous page stays on screen while the next one loads */
export const useSocialX = (q: SocialQuery) => {
  const tz = useAccount().web.displayTimezone;
  return useQuery({ queryKey: ['social-x', q, tz], queryFn: () => api.socialX(q), placeholderData: keepPreviousData });
};
export const useSocialYt = (q: SocialQuery) => {
  const tz = useAccount().web.displayTimezone;
  return useQuery({ queryKey: ['social-yt', q, tz], queryFn: () => api.socialYt(q), placeholderData: keepPreviousData });
};
export const useSocial = () => useSuspenseQuery({ queryKey: ['social'], queryFn: api.social }).data;
export const useOrcaAIData = () => useSuspenseQuery({ queryKey: ['orca-ai'], queryFn: api.orcaAI }).data;
export const useAlerts = () => useSuspenseQuery({ queryKey: ['alerts'], queryFn: api.alerts }).data;
export const usePlans = () => useSuspenseQuery({ queryKey: ['plans'], queryFn: api.plans }).data;
/** locked features: what they already found for the user (counts only) */
export const useTeasers = () => useSuspenseQuery({ queryKey: ['teasers'], queryFn: api.teasers }).data;
/** what each plan includes + the comparison table */
export const usePlanCatalog = () => useSuspenseQuery({ queryKey: ['plans', 'catalog'], queryFn: api.planCatalog }).data;
export const useProfile = () => useSuspenseQuery({ queryKey: ['profile'], queryFn: api.profile }).data;

const PLAN_DEPENDENT = ['account', 'alerts', 'news', 'news-page', 'ideas', 'social', 'social-x', 'social-yt', 'signals', 'notices', 'orca-ai', 'ai-quota', 'teasers'];
/** Profile action → writes the returned profile into the cache. */
export function useProfileAction<A extends unknown[]>(fn: (...a: A) => Promise<Profile>) {
  const qc = useQueryClient();
  // the plan can change (trial, payment, cancel…): the account follows it
  // a new plan opens or closes features: every plan-dependent query is fetched again
  const m = useMutation({ mutationFn: (a: A) => fn(...a), onSuccess: (p) => { qc.setQueryData(['profile'], p); void qc.invalidateQueries({ predicate: (q) => PLAN_DEPENDENT.includes(String(q.queryKey[0])) }); } });
  return { run: (...a: A) => m.mutateAsync(a), pending: m.isPending, error: m.error?.message ?? null, reset: m.reset };
}

/**
 * Alert setting change: applied to the cache at once (no waiting, no
 * re-render of the page), confirmed or rolled back by the API response.
 */
export function useAlertsAction<A extends unknown[]>(fn: (...a: A) => Promise<Alerts>, optimistic: (cur: Alerts, ...a: A) => Alerts) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationKey: ['alerts'],
    mutationFn: (a: A) => fn(...a),
    onMutate: async (a: A) => {
      await qc.cancelQueries({ queryKey: ['alerts'] });
      const prev = qc.getQueryData<Alerts>(['alerts']);
      if (prev) qc.setQueryData(['alerts'], optimistic(prev, ...a));
      return { prev };
    },
    onError: (_e, _a, ctx) => { if (ctx?.prev) qc.setQueryData(['alerts'], ctx.prev); },
    // with several quick changes in flight, only the last answer is applied
    // Settings shows the same Price / Whale switches: the account follows
    onSuccess: (next) => { if (qc.isMutating({ mutationKey: ['alerts'] }) <= 1) { qc.setQueryData(['alerts'], next); void qc.invalidateQueries({ queryKey: ['account'] }); } },
  });
  return { run: (...a: A) => m.mutateAsync(a), pending: m.isPending };
}

/**
 * Account setting change (coins, deliveries, AI usage): optimistic in the
 * cache — every page that reads the account updates at once — then confirmed
 * or rolled back by the API.
 */
export function useAccountAction<A extends unknown[]>(fn: (...a: A) => Promise<Account>, optimistic: (cur: Account, ...a: A) => Account) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationKey: ['account'],
    mutationFn: (a: A) => fn(...a),
    onMutate: async (a: A) => {
      await qc.cancelQueries({ queryKey: ['account'] });
      const prev = qc.getQueryData<Account>(['account']);
      if (prev) qc.setQueryData(['account'], optimistic(prev, ...a));
      return { prev };
    },
    onError: (_e, _a, ctx) => { if (ctx?.prev) qc.setQueryData(['account'], ctx.prev); },
    onSuccess: (next) => {
      if (qc.isMutating({ mutationKey: ['account'] }) <= 1) {
        qc.setQueryData(['account'], next);
        // Account-level switches (X/YouTube included) immediately refresh every
        // personalized surface; no second Web-only feature state exists.
        void qc.invalidateQueries({ predicate: (q) => ['social', 'social-x', 'social-yt', 'signals', 'notices'].includes(String(q.queryKey[0])) });
      }
    },
  });
  return { run: (...a: A) => m.mutateAsync(a), pending: m.isPending, error: m.error?.message ?? null };
}

/** Portfolio change (a purchase added / corrected / removed): optimistic, then the server's aggregate. */
export function usePortfolioAction<A extends unknown[]>(fn: (...a: A) => Promise<Portfolio>, optimistic: (cur: Portfolio, ...a: A) => Portfolio) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationKey: ['portfolio'],
    mutationFn: (a: A) => fn(...a),
    onMutate: async (a: A) => {
      await qc.cancelQueries({ queryKey: ['portfolio'] });
      const prev = qc.getQueryData<Portfolio>(['portfolio']);
      // the ORCA API values positions on the server: wait for its answer instead of a browser estimate
      if (prev && !prev.totals) qc.setQueryData(['portfolio'], optimistic(prev, ...a));
      return { prev };
    },
    onError: (_e, _a, ctx) => { if (ctx?.prev) qc.setQueryData(['portfolio'], ctx.prev); },
    onSuccess: (next) => { if (qc.isMutating({ mutationKey: ['portfolio'] }) <= 1) qc.setQueryData(['portfolio'], next); },
  });
  return { run: (...a: A) => m.mutateAsync(a), pending: m.isPending };
}
